import { Link } from '@tanstack/react-router';
import {
    Brain,
    CalendarCheck,
    CheckCircle2,
    CircleHelp,
    Contact,
    FileText,
    Flag,
    LogIn,
    LogOut,
    type LucideIcon,
    MessageSquareReply,
    XCircle,
} from 'lucide-react';
import { useState } from 'react';

import { TimelineEventRow } from '@apps/web/components/ui/timeline_event_row';
import { cn } from '@apps/web/lib/utils';

type LedgerTimelineEventKind =
    | 'agency_escalation'
    | 'callout_response'
    | 'caregiver_availability'
    | 'caregiver_memory'
    | 'clock_in'
    | 'clock_out'
    | 'contact_card'
    | 'shift_cancellation'
    | 'shift_cancellation_clarification'
    | 'shift_cancellation_confirmation_request'
    | 'shift_cancellation_selection_prompt'
    | 'shift_confirmation_response'
    | 'sms_action_group';

type LedgerTimelineSentencePartKind = Exclude<
    LedgerTimelineEventKind,
    'sms_action_group'
>;

type LedgerTimelineNavigation =
    | {
          readonly kind: 'activity_event';
          readonly id: string;
      }
    | {
          readonly kind: 'caregiver_memory';
          readonly id: string;
      };

type LedgerTimelineItemProps = {
    readonly label: string;
    readonly kind?: LedgerTimelineEventKind;
    readonly sentenceParts?: ReadonlyArray<{
        readonly kind?: LedgerTimelineSentencePartKind;
        readonly text: string;
        readonly navigation?: LedgerTimelineNavigation | null;
    }>;
    readonly onOpenCaregiverMemory?: (caregiverId: string) => void;
};

const ledgerActionClassName = cn(
    'text-muted-foreground inline bg-transparent p-0 align-baseline text-[12.5px] leading-[19px]',
    'hover:text-foreground/80 underline-offset-2 transition-colors hover:underline focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
);
const COLLAPSED_LEDGER_PART_COUNT = 3;
const ledgerIconByKind: Record<LedgerTimelineSentencePartKind, LucideIcon> = {
    agency_escalation: Flag,
    callout_response: MessageSquareReply,
    caregiver_availability: CalendarCheck,
    caregiver_memory: Brain,
    clock_in: LogIn,
    clock_out: LogOut,
    contact_card: Contact,
    shift_cancellation: XCircle,
    shift_cancellation_clarification: CircleHelp,
    shift_cancellation_confirmation_request: CircleHelp,
    shift_cancellation_selection_prompt: CircleHelp,
    shift_confirmation_response: CheckCircle2,
};

type LedgerSentencePart = {
    readonly kind: LedgerTimelineSentencePartKind | null;
    readonly text: string;
    readonly navigation: LedgerTimelineNavigation | null;
};

export function LedgerTimelineItem({
    label,
    kind,
    sentenceParts,
    onOpenCaregiverMemory,
}: LedgerTimelineItemProps) {
    const [expanded, setExpanded] = useState(false);
    const parts =
        sentenceParts?.flatMap((part): Array<LedgerSentencePart> => {
            const text = part.text.trim();
            return text
                ? [
                      {
                          kind: part.kind ?? null,
                          text,
                          navigation: part.navigation ?? null,
                      },
                  ]
                : [];
        }) ?? [];
    const hasOverflowParts = parts.length > COLLAPSED_LEDGER_PART_COUNT;
    const isCollapsed = !expanded && hasOverflowParts;
    const visibleParts = isCollapsed
        ? parts.slice(0, COLLAPSED_LEDGER_PART_COUNT)
        : parts;
    const toggleExpansionLabel = isCollapsed
        ? 'Show all ledger events'
        : 'Show fewer ledger events';

    function renderPart(part: LedgerSentencePart) {
        const navigation = part.navigation;
        if (navigation?.kind === 'caregiver_memory' && onOpenCaregiverMemory) {
            return (
                <button
                    type="button"
                    className={ledgerActionClassName}
                    onClick={() => onOpenCaregiverMemory(navigation.id)}
                >
                    {part.text}
                </button>
            );
        }
        if (navigation?.kind === 'activity_event') {
            return (
                <Link
                    className={ledgerActionClassName}
                    to="/activity"
                    search={{ eventId: navigation.id, view: 'all' }}
                >
                    {part.text}
                </Link>
            );
        }
        return <span>{part.text}</span>;
    }

    function renderIcon(eventKind: LedgerTimelineEventKind | null) {
        const Icon =
            eventKind && eventKind !== 'sms_action_group'
                ? ledgerIconByKind[eventKind]
                : FileText;
        return (
            <Icon
                aria-hidden="true"
                data-testid={`ledger-event-icon-${eventKind ?? 'sms_action_group'}`}
            />
        );
    }

    function renderLedgerRow(part: LedgerSentencePart) {
        // Two escalation parts can share identical text (matching truncated
        // titles), so key on the navigation target when there is one.
        const partKey = part.navigation
            ? `${part.kind ?? 'ledger'}:${part.navigation.kind}:${part.navigation.id}`
            : `${part.kind ?? 'ledger'}:${part.text}`;
        return (
            <TimelineEventRow
                key={partKey}
                className="justify-center"
                icon={renderIcon(part.kind)}
            >
                <span className="min-w-0">Phoebe {renderPart(part)}</span>
            </TimelineEventRow>
        );
    }

    if (parts.length > 0) {
        return (
            <div className="my-2 space-y-1">
                {visibleParts.map(renderLedgerRow)}
                {hasOverflowParts && (
                    <div className="flex justify-center px-2">
                        <button
                            type="button"
                            className={ledgerActionClassName}
                            aria-expanded={expanded}
                            aria-label={toggleExpansionLabel}
                            onClick={() => setExpanded((current) => !current)}
                        >
                            …
                        </button>
                    </div>
                )}
            </div>
        );
    }

    return (
        <TimelineEventRow
            className="my-2 justify-center"
            icon={renderIcon(kind ?? null)}
        >
            <span className="min-w-0">{label}</span>
        </TimelineEventRow>
    );
}
